import { type ReactNode, useCallback, useState } from "react";
import { Box } from "@dealer-center/core-components";
import { MyInfoStatusContext } from "./myInfoStatusContext";
import { SCREEN_READER_ONLY } from "./screenReaderOnly";

export interface StatusRegionProps {
  children: ReactNode;
}

/**
 * The page's single `aria-live` status region, plus the channel tabs use to write into it (T069).
 *
 * **Why one region at the shell rather than one per tab.** A live region has to already be in the
 * document when its text changes — a region that mounts *carrying* its message is not reliably
 * announced, because assistive technology sees a new node rather than a changed one. A per-tab region
 * mounts at the same moment as the tab's first state, so its first announcement (typically "loading")
 * is the least reliable one. This region is mounted on page load and outlives every tab switch, so
 * every transition after it is a text change.
 *
 * It also generalises T055, which built a region for Login History alone because of that tab's ~20s
 * latency. The other three tabs had loading and failure states with **no** announcement at all;
 * routing all four through `TabState` gives them one without any per-tab code.
 *
 * `useToaster()` is not an alternative — it renders no live region, so its toasts are never spoken
 * (`.claude/rules/accessibility.md`).
 */
export const StatusRegion = ({ children }: StatusRegionProps) => {
  const [message, setMessage] = useState("");

  /*
   * Stable, and bails out when the message is unchanged.
   *
   * Both matter: `useAnnounceStatus` calls this from an effect keyed on `[announce, message]`, so an
   * unstable identity would re-announce on every render, and re-setting the same string would be a
   * wasted render at best. Returning `previous` unchanged makes React skip the update entirely.
   */
  const announce = useCallback(
    (next: string) =>
      setMessage(previous => (previous === next ? previous : next)),
    []
  );

  return (
    <>
      {/*
       * `polite`, not `assertive`: these are progress reports, and assertive interrupts whatever the
       * user is currently reading. Rendered before `children` so it is in the document ahead of
       * anything that can write to it.
       */}
      <Box
        aria-live={"polite"}
        data-testid={"myinfo-status"}
        role={"status"}
        sx={SCREEN_READER_ONLY}
      >
        {message}
      </Box>

      <MyInfoStatusContext.Provider value={announce}>
        {children}
      </MyInfoStatusContext.Provider>
    </>
  );
};
